

import {
  getSimplifiedIrlBerRatingColor,
  SimplifiedIrlBerRatings,
} from "@/helpers";
import { Expression, Map, NavigationControl, Style } from "mapbox-gl";
import {computed, onUnmounted, ref} from "vue";

import mapStyle from '@/assets/map-styles/positron-gl-style.json';
const map = ref<Map>();
const mapLoaded = computed(() => map.value);

export function useMap() {


  function initializeMap(mapContainerElement: HTMLElement): Map {
    map.value = new Map({
      container: mapContainerElement,
      interactive: true,
      bounds: [-11.05, 51.06, -5.21, 55.42],
      style: mapStyle as Style,
    });

    map.value.addControl(new NavigationControl(), "top-left");

    map.value.on("load", () => {
      if (!map.value) return;

      addEnergyRatingLayer(map.value);
      map.value.getCanvas().style.cursor = "pointer";
      map.value.resize();

      const attrRoot = map.value.getContainer().querySelector<HTMLElement>('.mapboxgl-ctrl-attrib');
      if (attrRoot) {
        const run = () => fixAttrAccessibility(attrRoot);
        run();
        const mo = new MutationObserver(run);
        mo.observe(attrRoot, { childList: true, subtree: true });
        map.value.once('remove', () => mo.disconnect());
        onUnmounted(() => mo.disconnect());
      }
    });
    return map.value;
  }
  function fixAttrAccessibility(root: HTMLElement) {
    const inner = root.querySelector<HTMLDivElement>('.mapboxgl-ctrl-attrib-inner');
    if (!inner) return;
    inner.setAttribute('role', 'list');
    inner.querySelectorAll('a').forEach(a => a.setAttribute('role', 'listitem'));
  }

  function addEnergyRatingLayer(map: Map) {
    const tileSet = "energy_ratings_roi";
    const layerSource = "energy_ratings";
    map.addSource(tileSet, {
      type: 'vector',
      tiles: [
        'https://assets.berwow.ie/vector_tiles/energy_ratings_roi/{z}/{x}/{y}.pbf',
      ],
      minzoom: 0,
      maxzoom: 23,
    });

    const colorExpression = getBerMapColorExpression();

    map.addLayer({
      id: tileSet + "_" + layerSource,
      type: "fill",
      source: tileSet,
      "source-layer": layerSource,
      paint: {
        "fill-color": colorExpression,
        "fill-opacity": 1,
        "fill-outline-color": "rgba(0, 0, 0, 0)",
      },
      minzoom: 0,
      maxzoom: 23,
    });

    //display under roads - layer name may changed based on the OSM styles applied
    if (map.getLayer("transportation"))
      map.moveLayer(tileSet + "_" + layerSource, "transportation");
    else if (map.getLayer("tunnel_motorway_casing"))
      map.moveLayer(tileSet + "_" + layerSource, "tunnel_motorway_casing");
  }

  function getBerMapColorExpression(): Expression {
    const ratingFields = [
      "median_ber_rating",
      "MEDIAN_BER_RATING",
      "median_ber",
      "MEDIAN_BER",
    ];

    return (["case"] as Expression)
      .concat(
        ratingFields
          .map((field) => [["has", field], getRatingCodeColorExpression(field)])
          .flat()
      )
      .concat(["#c0c0c0"]) as Expression;
  }

  function getRatingCodeColorExpression(field: string): Expression {
    return [
      "match",
      ["get", field],
      ["A0", "a0"],
      getSimplifiedIrlBerRatingColor("A0"),
      ["A", "a", "A1", "a1", "A2", "a2", "A3", "a3"],
      getSimplifiedIrlBerRatingColor("A"),
      ["B", "b", "B1", "b1", "B2", "b2", "B3", "b3"],
      getSimplifiedIrlBerRatingColor("B"),
      ["C", "c", "C1", "c1", "C2", "c2", "C3", "c3"],
      getSimplifiedIrlBerRatingColor("C"),
      ["D", "d", "D1", "d1", "D2", "d2"],
      getSimplifiedIrlBerRatingColor("D"),
      ["E", "e", "E1", "e1", "E2", "e2"],
      getSimplifiedIrlBerRatingColor("E"),
      ["F", "f"],
      getSimplifiedIrlBerRatingColor("F"),
      ["G", "g"],
      getSimplifiedIrlBerRatingColor("G"),
      getNumericBerColorExpression(field),
    ] as Expression;
  }

  function getNumericBerColorExpression(field: string): Expression {
    const numericValue = ["to-number", ["get", field], -1];
    // A0 also requires ZEB criteria, so numeric-only tile data falls back to A.
    const [, ...ratingsWithoutA0] = [...SimplifiedIrlBerRatings];

    return (["case"] as Expression)
      .concat(
        ratingsWithoutA0
          .filter(([code]) => code !== "G")
          .map(([, rating]) => [
            ["all", [">=", numericValue, 0], ["<=", numericValue, rating.level]],
            rating.color,
          ])
          .flat()
      )
      .concat([
        [">", numericValue, 375],
        getSimplifiedIrlBerRatingColor("G"),
        "#c0c0c0",
      ]) as Expression;
  }

  return { initializeMap, map, mapLoaded };
}
